// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage PRODUCTION-CUTTING-PLANNER-1 · pure UI helpers.
// Area maths mirror the backend (CEIL dims → divisors → 3dp); thickness
// formatting + colour and CEP detection match the CNC export rules.
// ════════════════════════════════════════════════════════════════════

export const SQFT_DIVISOR = 92903.04;
export const SQMT_DIVISOR = 1000000;
export const CNC_CEP_ADD_MM = 4;

export const PERM = Object.freeze({
  VIEW: 'erp.production.cutting_planner.view',
  MANAGE: 'erp.production.cutting_planner.manage',
  EXPORT: 'erp.production.cutting_planner.export',
  DELETE: 'erp.production.cutting_planner.delete',
  ISSUE_STOCK: 'erp.production.cutting_planner.issue_stock'
});

export function hasPermission(me, key) {
  if (!me) return false;
  const list = me.permissions || [];
  return list.includes('*') || list.includes(key);
}

function toNum(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }
export function round3(v) { return Math.round((toNum(v) + Number.EPSILON) * 1000) / 1000; }

// Area from raw mm — width/height CEIL'd BEFORE the maths (per spec).
export function computeAreas(widthMm, heightMm) {
  const w = Math.ceil(toNum(widthMm));
  const h = Math.ceil(toNum(heightMm));
  const mm2 = w * h;
  return { sqft: round3(mm2 / SQFT_DIVISOR), sqmt: round3(mm2 / SQMT_DIVISOR) };
}

// CEP is encoded in the item description ("… WITH CEP" / "#CEP"), matching the
// proforma engine; an explicit edgeFinish === 'CEP' also counts.
export function isCep(itemDesc, edgeFinish) {
  if (edgeFinish && String(edgeFinish).trim().toUpperCase() === 'CEP') return true;
  return /\bCEP\b/i.test(String(itemDesc || ''));
}

export function cncDimension(mm, cep) {
  return cep ? toNum(mm) + CNC_CEP_ADD_MM : toNum(mm);
}

// Round a mm value UP to the whole millimetre (the cut size is never rounded
// down). null/blank → null.
export function mmCeil(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.ceil(n) : null;
}

function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }

// mm → inches expressed as a 1/16 fraction, rounded to the nearest 1/16:
//   1001 → "39 7/16",  305 → "12",  25.4 → "1",  9 → "3/8".
// The whole part and the reduced fraction are joined by a space. Blank → "—".
export function mmToInchFraction(mm) {
  const n = Number(mm);
  if (!Number.isFinite(n)) return '—';
  const sixteenths = Math.round((n / 25.4) * 16);
  const whole = Math.floor(sixteenths / 16);
  const rem = sixteenths % 16;
  if (rem === 0) return `${whole}`;
  const g = gcd(rem, 16);
  const frac = `${rem / g}/${16 / g}`;
  return whole > 0 ? `${whole} ${frac}` : frac;
}

// The stock name WITHOUT a trailing sheet-size token, e.g. "CLEAR 2140x3300" →
// "CLEAR". A name that carries no NNxNN size ("12MM BRONZE … - SHEET") is left
// as-is; a name that is ONLY a size falls back to the original. Blank → null.
export function stockDisplayName(name) {
  const s = name == null ? '' : String(name).trim();
  if (!s) return null;
  const stripped = s.replace(/[\s,()-]*\b\d{2,}\s*[x×*]\s*\d{2,}\s*(?:mm)?\s*$/i, '').trim();
  return stripped || s;
}

// 12.00 → "12", 5.50 → "5.5", null → "".
export function formatThickness(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return String(Number(n.toFixed(2)));
}

// Ant Design Tag colour keyed to thickness (mm). Falls back to 'default'.
const THICKNESS_COLORS = {
  4: 'geekblue', 5: 'geekblue', 6: 'blue', 8: 'cyan',
  10: 'gold', 12: 'purple', 15: 'magenta', 19: 'red'
};
export function thicknessColor(v) {
  const n = Math.round(Number(v));
  return THICKNESS_COLORS[n] || 'default';
}

// AntD-preset-derived {bg, fg} for a self-contained thickness/edge PILL. Used
// where the column repeats one colour every row (Thk / Edge in the piece grid):
// a real <Tag> there would trip the global "tint the whole cell" rule and paint
// a vertical band down the column, so we render a plain coloured span instead.
const CHIP_HEX = {
  geekblue: { bg: '#f0f5ff', fg: '#1d39c4' },
  blue:     { bg: '#e6f4ff', fg: '#0958d9' },
  cyan:     { bg: '#e6fffb', fg: '#08979c' },
  gold:     { bg: '#fffbe6', fg: '#d48806' },
  purple:   { bg: '#f9f0ff', fg: '#531dab' },
  magenta:  { bg: '#fff0f6', fg: '#c41d7f' },
  red:      { bg: '#fff1f0', fg: '#cf1322' },
  default:  { bg: '#f5f5f5', fg: '#595959' }
};
export function thicknessChip(v) { return CHIP_HEX[thicknessColor(v)] || CHIP_HEX.default; }
export const EDGE_CHIP = CHIP_HEX.blue;

// Ant Design Tag colour keyed to glass CATEGORY name (from the Glass Category
// Master). Case-insensitive; unknown / blank → 'default'. Mirrors the master's
// vocabulary so the Category KPI bars read consistently with the item popups.
const CATEGORY_COLORS = {
  'CLEAR': 'blue',
  'EXTRA CLEAR': 'geekblue',
  'TINTED': 'gold',
  'REFLECTIVE': 'cyan',
  'MIRROR': 'purple',
  'LACQUERED': 'volcano',
  'FLUTED': 'lime',
  'LAMINATED': 'green',
  'INSULATED': 'geekblue',
  'SPECIAL': 'magenta',
  'OTHER': 'default',
  'UNSPECIFIED': 'default'
};
export function categoryColor(name) {
  if (name == null) return 'default';
  return CATEGORY_COLORS[String(name).trim().toUpperCase()] || 'default';
}

// Summary totals for the planner grid.
export function summarizeItems(items) {
  const list = Array.isArray(items) ? items : [];
  let sqft = 0; let sqmt = 0;
  const thicknesses = new Set(); const stocks = new Set();
  for (const it of list) {
    sqft += toNum(it.sqft); sqmt += toNum(it.sqmt);
    if (it.thickness != null) thicknesses.add(Number(it.thickness));
    if (it.stockName) stocks.add(it.stockName);
  }
  return {
    totalPcs: list.length,
    totalSqft: round3(sqft),
    totalSqmt: round3(sqmt),
    thickness: thicknesses.size === 1 ? [...thicknesses][0] : null,
    stockName: stocks.size === 1 ? [...stocks][0] : null
  };
}

// Group tracker rows by thickness for the KPI strip.
export function groupByThickness(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const map = new Map();
  for (const r of list) {
    const key = r.thickness == null ? '—' : Number(r.thickness);
    const g = map.get(key) || { thickness: key, plans: 0, actualPcs: 0, actualSqmt: 0, completedPcs: 0, completedSqmt: 0, pendingPcs: 0, pendingSqmt: 0 };
    g.plans += 1;
    g.actualPcs += toNum(r.actualPcs); g.actualSqmt += toNum(r.actualSqmt);
    g.completedPcs += toNum(r.completedPcs); g.completedSqmt += toNum(r.completedSqmt);
    g.pendingPcs += toNum(r.pendingPcs); g.pendingSqmt += toNum(r.pendingSqmt);
    map.set(key, g);
  }
  return [...map.values()].map((g) => ({
    ...g, actualSqmt: round3(g.actualSqmt), completedSqmt: round3(g.completedSqmt), pendingSqmt: round3(g.pendingSqmt)
  }));
}
